// src/pages/Home.jsx

import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import Typed from "typed.js";

import {
  FaArrowRight,
  FaCheck,
  FaCode,
  FaMobileAlt,
  FaBullhorn,
  FaLayerGroup,
  FaRocket,
  FaPalette,
  FaShieldAlt,
  FaChartLine,
  FaStar,
  FaBolt,
} from "react-icons/fa";

export default function Home() {
  const heroRef = useRef(null);
  const visualRef = useRef(null);
  const typedRef = useRef(null);

  // =========================================================
  // AOS + TYPED
  // =========================================================

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: "ease-out-cubic",
      offset: 70,
    });

    if (!typedRef.current) return;

    const typed = new Typed(typedRef.current, {
      strings: [
        "Digital Experiences",
        "Modern Websites",
        "Mobile Products",
        "Business Solutions",
      ],
      typeSpeed: 55,
      backSpeed: 35,
      backDelay: 1800,
      startDelay: 400,
      loop: true,
      showCursor: true,
      cursorChar: "|",
    });

    return () => {
      typed.destroy();
    };
  }, []);

  // =========================================================
  // HERO PARALLAX
  // =========================================================

  useEffect(() => {
    const hero = heroRef.current;
    const visual = visualRef.current;

    if (!hero || !visual) return;

    const handleMove = (e) => {
      if (window.innerWidth < 1024) return;

      const rect = hero.getBoundingClientRect();

      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;

      visual.style.transform = `
        perspective(1200px)
        rotateX(${-y * 3}deg)
        rotateY(${x * 5}deg)
        translate3d(${x * 5}px, ${y * 5}px, 0)
      `;
    };

    const reset = () => {
      visual.style.transform = `
        perspective(1200px)
        rotateX(0deg)
        rotateY(0deg)
        translate3d(0, 0, 0)
      `;
    };

    hero.addEventListener("mousemove", handleMove);
    hero.addEventListener("mouseleave", reset);

    return () => {
      hero.removeEventListener("mousemove", handleMove);
      hero.removeEventListener("mouseleave", reset);
    };
  }, []);

  // =========================================================
  // SERVICES
  // =========================================================

  const services = [
    {
      icon: <FaCode />,
      title: "Web Development",
      text: "Fast, responsive and SEO-friendly websites built around your business.",
    },
    {
      icon: <FaMobileAlt />,
      title: "Mobile Apps",
      text: "Modern Android and iOS applications designed for real users.",
    },
    {
      icon: <FaBullhorn />,
      title: "Digital Marketing",
      text: "SEO, advertising and growth strategies that bring your brand forward.",
    },
    {
      icon: <FaLayerGroup />,
      title: "CRM & Automation",
      text: "Smart business systems that simplify operations and save time.",
    },
  ];

  // =========================================================
  // WHY US
  // =========================================================

  const reasons = [
    {
      icon: <FaRocket />,
      title: "Fast Delivery",
      text: "Focused development without unnecessary delays.",
    },
    {
      icon: <FaPalette />,
      title: "Modern UI/UX",
      text: "Clean interfaces that look beautiful and feel effortless.",
    },
    {
      icon: <FaShieldAlt />,
      title: "Reliable Technology",
      text: "Secure, maintainable and scalable digital solutions.",
    },
    {
      icon: <FaChartLine />,
      title: "Growth Focused",
      text: "Technology built around your actual business goals.",
    },
  ];

  return (
    <main
      ref={heroRef}
      className="min-h-screen overflow-hidden bg-white font-[Manrope] text-slate-900"
    >
      {/* =====================================================
          GLOBAL BACKGROUND
      ====================================================== */}

      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -left-52 top-0 h-[500px] w-[500px] rounded-full bg-blue-100/60 blur-[120px]" />

        <div className="absolute -right-52 top-[20%] h-[600px] w-[600px] rounded-full bg-sky-100/60 blur-[140px]" />

        <div className="absolute bottom-[-180px] left-[35%] h-[500px] w-[500px] rounded-full bg-indigo-100/50 blur-[130px]" />
      </div>

      {/* =====================================================
          HERO
      ====================================================== */}

      <section className="relative flex min-h-[720px] items-center px-5 pb-20 pt-28 sm:px-8 lg:min-h-[calc(100vh-70px)] lg:px-12">
        {/* Floating Details */}

        <span className="absolute left-[8%] top-[28%] h-2 w-2 animate-pulse rounded-full bg-blue-500/70" />

        <span className="absolute right-[15%] top-[25%] h-2 w-2 animate-ping rounded-full bg-sky-400/70" />

        <span className="absolute bottom-[20%] left-[45%] h-1.5 w-1.5 animate-pulse rounded-full bg-indigo-500/70" />

        <div className="mx-auto grid w-full max-w-7xl items-center gap-12 lg:grid-cols-[0.95fr_1.05fr]">
          {/* =================================================
              HERO LEFT
          ================================================= */}

          <div
            data-aos="fade-right"
            className="relative z-10 text-center lg:text-left"
          >
            {/* Badge */}

            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50/80 px-4 py-2 shadow-sm backdrop-blur">
              <span className="relative flex h-2 w-2">
                <span className="absolute h-full w-full animate-ping rounded-full bg-blue-500 opacity-50" />
                <span className="relative h-2 w-2 rounded-full bg-blue-600" />
              </span>

              <span className="text-[9px] font-extrabold uppercase tracking-[0.2em] text-blue-600">
                Digital Solutions For Modern Business
              </span>
            </div>

            {/* Heading */}

            <h1 className="mx-auto max-w-[680px] text-[38px] font-extrabold leading-[1.08] tracking-[-0.045em] text-slate-950 sm:text-5xl md:text-6xl lg:mx-0 lg:text-[58px] xl:text-[62px]">
              We Build
              <br />

              <span className="relative inline-block min-h-[1.15em] bg-gradient-to-r from-blue-600 via-indigo-600 to-sky-500 bg-clip-text text-transparent">
                <span ref={typedRef} />

                <span className="absolute -bottom-1 left-0 h-[3px] w-14 rounded-full bg-blue-600" />
              </span>

              <br />

              <span className="text-slate-950">
                That Grow Businesses.
              </span>
            </h1>

            {/* Description */}

            <p className="mx-auto mt-6 max-w-xl text-sm leading-7 text-slate-500 sm:text-base lg:mx-0">
              PrimeAppConnect creates websites, mobile apps, CRM systems and
              digital marketing solutions that help businesses look better,
              work smarter and grow faster.
            </p>

            {/* =================================================
                HERO BUTTONS
            ================================================= */}

            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
              {/* Start Project */}

              <Link
                to="/contact"
                className="group relative inline-flex h-13 items-center justify-center gap-3 overflow-hidden rounded-xl bg-blue-600 px-7 text-sm font-extrabold text-white shadow-[0_14px_35px_rgba(37,99,235,.22)] transition-all duration-300 hover:-translate-y-1 hover:bg-blue-700 hover:shadow-[0_20px_45px_rgba(37,99,235,.30)]"
              >
                <span className="relative z-10">
                  Start Your Project
                </span>

                <FaArrowRight className="relative z-10 text-xs transition-transform duration-300 group-hover:translate-x-1" />

                <span className="absolute inset-y-0 left-[-100%] w-1/2 -skew-x-12 bg-white/20 transition-all duration-700 group-hover:left-[130%]" />
              </Link>

              {/* Explore Services */}

              <Link
                to="/services"
                className="group inline-flex h-13 items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-7 text-sm font-bold text-slate-700 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-200 hover:text-blue-600 hover:shadow-md"
              >
                Explore Services

                <FaArrowRight className="text-xs transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>

            {/* Trust Points */}

            <div className="mt-7 flex flex-wrap justify-center gap-x-6 gap-y-3 lg:justify-start">
              {[
                "Modern Design",
                "Clean Development",
                "Long-Term Support",
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-2 text-[11px] font-medium text-slate-500"
                >
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-50">
                    <FaCheck className="text-[7px] text-blue-600" />
                  </span>

                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* =================================================
              HERO VISUAL
          ================================================= */}

          <div
            data-aos="fade-left"
            className="relative flex min-h-[470px] items-center justify-center"
          >
            {/* Main Glow */}

            <div className="absolute h-[280px] w-[280px] animate-pulse rounded-full bg-blue-200/50 blur-[90px] sm:h-[400px] sm:w-[400px]" />

            {/* Static Ring */}

            <div className="absolute h-[310px] w-[310px] rounded-full border border-blue-100 sm:h-[470px] sm:w-[470px]" />

            {/* Animated Ring */}

            <div className="absolute h-[390px] w-[390px] animate-[spin_35s_linear_infinite] rounded-full border border-dashed border-blue-100 sm:h-[540px] sm:w-[540px]" />

            {/* Visual */}

            <div
              ref={visualRef}
              className="relative z-10 w-full max-w-[510px] transition-transform duration-300 ease-out"
            >
              <div className="group relative overflow-hidden rounded-[30px] border-[7px] border-white bg-white shadow-[0_30px_80px_rgba(37,99,235,.16)]">
                <img
                  src="https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1200&q=85"
                  alt="Digital technology team collaborating"
                  className="h-[380px] w-full object-cover transition duration-700 group-hover:scale-105 sm:h-[450px]"
                />

                {/* Overlay */}

                <div className="absolute inset-0 bg-gradient-to-t from-blue-950/50 via-blue-950/5 to-transparent" />

                {/* Shine */}

                <div className="pointer-events-none absolute inset-y-0 left-[-100%] w-[35%] -skew-x-12 bg-white/20 blur-sm transition-all duration-1000 group-hover:left-[150%]" />

                {/* Bottom Card */}

                <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/30 bg-white/90 p-4 shadow-xl backdrop-blur-xl transition duration-300 group-hover:-translate-y-1">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg">
                        <FaBolt />
                      </div>

                      <div>
                        <p className="text-xs font-extrabold text-slate-900">
                          Digital Growth
                        </p>

                        <p className="mt-0.5 text-[9px] font-medium text-slate-500">
                          Strategy • Design • Technology
                        </p>
                      </div>
                    </div>

                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-[8px] font-extrabold tracking-wide text-emerald-600">
                      ACTIVE
                    </span>
                  </div>
                </div>
              </div>

              {/* Floating Card One */}

              <div className="absolute -left-5 top-12 hidden animate-[bounce_5s_ease-in-out_infinite] rounded-2xl border border-blue-100 bg-white px-4 py-3 shadow-[0_15px_40px_rgba(37,99,235,.15)] sm:block">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <FaCode />
                  </div>

                  <div>
                    <p className="text-[10px] font-extrabold text-slate-800">
                      Modern Technology
                    </p>

                    <p className="mt-1 text-[8px] font-medium text-slate-400">
                      Built to perform
                    </p>
                  </div>
                </div>
              </div>

              {/* Floating Card Two */}

              <div className="absolute -right-5 bottom-20 hidden animate-[bounce_6s_ease-in-out_infinite_reverse] rounded-2xl border border-indigo-100 bg-white px-4 py-3 shadow-[0_15px_40px_rgba(37,99,235,.15)] sm:block">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                    <FaStar />
                  </div>

                  <div>
                    <p className="text-[10px] font-extrabold text-slate-800">
                      Premium Experience
                    </p>

                    <p className="mt-1 text-[8px] font-medium text-slate-400">
                      Built for your brand
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          SERVICE STRIP
      ====================================================== */}

      <section className="border-y border-slate-100 bg-slate-50/70 px-5 py-6 sm:px-8">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-9 gap-y-3">
          {[
            "Web Development",
            "Mobile Apps",
            "UI / UX",
            "CRM Systems",
            "Digital Marketing",
          ].map((item) => (
            <span
              key={item}
              className="flex items-center gap-2 text-[10px] font-extrabold uppercase tracking-[0.12em] text-slate-400 transition-colors duration-300 hover:text-blue-600"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />

              {item}
            </span>
          ))}
        </div>
      </section>

      {/* =====================================================
          SERVICES
      ====================================================== */}

      <section className="px-5 py-24 sm:px-8 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="OUR SERVICES"
            title="Everything Your"
            highlight="Business Needs."
            description="From websites and apps to marketing and automation, we bring your digital presence together."
          />

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((service, index) => (
              <article
                key={service.title}
                data-aos="fade-up"
                data-aos-delay={index * 100}
                className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-500 hover:-translate-y-2 hover:border-blue-200 hover:shadow-[0_20px_50px_rgba(37,99,235,.10)]"
              >
                {/* Glow */}

                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-blue-100 opacity-0 blur-3xl transition duration-500 group-hover:opacity-100" />

                <div className="relative">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black text-slate-300">
                      0{index + 1}
                    </span>

                    <FaArrowRight className="text-xs text-slate-300 transition-all duration-300 group-hover:-rotate-45 group-hover:text-blue-600" />
                  </div>

                  <div className="mt-9 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-xl text-blue-600 transition-all duration-500 group-hover:rotate-3 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white">
                    {service.icon}
                  </div>

                  <h3 className="mt-6 text-lg font-extrabold tracking-tight text-slate-900">
                    {service.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-500">
                    {service.text}
                  </p>

                  {/* Service Details Link */}

                  <Link
                    to="/services"
                    className="mt-6 inline-flex items-center gap-2 text-xs font-extrabold text-blue-600"
                  >
                    Learn more

                    <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          WHY US
      ====================================================== */}

      <section className="relative overflow-hidden bg-[#f5f9ff] px-5 py-24 sm:px-8 lg:px-12">
        <div className="absolute -left-32 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full bg-blue-100/70 blur-[100px]" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[0.9fr_1.1fr]">
          {/* Left */}

          <div data-aos="fade-right">
            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-blue-600">
              WHY PRIMEAPPCONNECT
            </p>

            <h2 className="mt-4 text-4xl font-extrabold leading-[1.08] tracking-[-0.04em] text-slate-900 sm:text-5xl">
              Technology
              <br />

              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                With Purpose.
              </span>
            </h2>

            <p className="mt-6 max-w-lg text-sm leading-7 text-slate-500 sm:text-base">
              We combine strategy, design and technology to create digital
              products that look professional, perform beautifully and help
              businesses grow.
            </p>

            {/* About Link */}

            <Link
              to="/about"
              className="group mt-7 inline-flex items-center gap-3 rounded-xl bg-slate-900 px-6 py-3.5 text-sm font-extrabold text-white transition-all duration-300 hover:-translate-y-1 hover:bg-blue-600 hover:shadow-lg"
            >
              Discover Our Approach

              <FaArrowRight className="text-xs transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>

          {/* Right */}

          <div className="grid gap-4 sm:grid-cols-2">
            {reasons.map((reason, index) => (
              <div
                key={reason.title}
                data-aos="fade-up"
                data-aos-delay={index * 100}
                className="group rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-blue-200 hover:shadow-[0_18px_45px_rgba(37,99,235,.09)]"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition duration-300 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white">
                  {reason.icon}
                </div>

                <h3 className="mt-5 font-extrabold tracking-tight text-slate-900">
                  {reason.title}
                </h3>

                <p className="mt-2 text-xs leading-6 text-slate-500">
                  {reason.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          FINAL CTA
      ====================================================== */}

      <section className="px-5 py-24 sm:px-8 lg:px-12">
        <div
          data-aos="zoom-in"
          className="relative mx-auto max-w-6xl overflow-hidden rounded-[35px] bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 px-6 py-20 text-center shadow-[0_25px_80px_rgba(37,99,235,.25)] sm:px-12"
        >
          {/* Background Glow */}

          <div className="absolute -left-24 -top-24 h-72 w-72 animate-pulse rounded-full bg-white/10 blur-3xl" />

          <div className="absolute -bottom-32 -right-24 h-80 w-80 rounded-full bg-indigo-400/20 blur-3xl" />

          {/* Decorative Stars */}

          <FaStar className="absolute left-[12%] top-[25%] animate-pulse text-sm text-white/25" />

          <FaStar className="absolute right-[15%] top-[20%] animate-bounce text-xs text-white/20" />

          <FaStar className="absolute bottom-[25%] left-[20%] animate-pulse text-xs text-white/20" />

          <div className="relative">
            {/* CTA Badge */}

            <div className="mx-auto flex w-fit items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 backdrop-blur">
              <FaBolt className="text-[9px] text-blue-100" />

              <span className="text-[9px] font-black uppercase tracking-[0.2em] text-blue-100">
                Let's Build Something Great
              </span>
            </div>

            {/* CTA Heading */}

            <h2 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-[-0.04em] text-white sm:text-5xl md:text-6xl">
              Have An Idea?
              <br />

              <span className="text-blue-100">
                Let's Make It Real.
              </span>
            </h2>

            {/* CTA Description */}

            <p className="mx-auto mt-5 max-w-xl text-sm leading-7 text-blue-100/80 sm:text-base">
              Tell us what you're building and we'll help turn your idea into
              a digital experience your customers remember.
            </p>

            {/* CTA Link */}

            <Link
              to="/contact"
              className="group mt-8 inline-flex h-14 items-center gap-3 rounded-xl bg-white px-8 text-sm font-extrabold text-blue-700 shadow-xl transition-all duration-300 hover:-translate-y-1 hover:bg-blue-50 hover:shadow-2xl"
            >
              Start Your Project

              <FaArrowRight className="text-xs transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

// ================================================================
// SECTION HEADING
// ================================================================

function SectionHeading({
  eyebrow,
  title,
  highlight,
  description,
}) {
  return (
    <div
      data-aos="fade-up"
      className="mx-auto max-w-2xl text-center"
    >
      <p className="text-[10px] font-black uppercase tracking-[0.25em] text-blue-600">
        {eyebrow}
      </p>

      <h2 className="mt-4 text-4xl font-extrabold leading-[1.08] tracking-[-0.04em] text-slate-900 sm:text-5xl">
        {title}
        <br />

        <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
          {highlight}
        </span>
      </h2>

      <p className="mt-5 text-sm leading-7 text-slate-500 sm:text-base">
        {description}
      </p>
    </div>
  );
}